import { useEffect, useMemo, useState } from "react";

import type { AccountDetailVO, OPSUserRoleVO } from "@/api/accounts/type";
import { getEmployeeOrganizations } from "@/api/employees";
import { type OrganizationDetailVO } from "@/api/system/organizations/type";

import { getOpsUserRoleKey } from "../../../utils";

/**
 * 管理账号身份权限的组织与角色选中态
 * @param userId 业务用户 ID
 * @param accountDetail 账号详情（含 opsUserRoleList）
 * @param enabled 是否启用（Tab 激活或弹窗打开时才拉组织并重置选中）
 */
export const useAccountRolesSelection = (
  userId: string | undefined,
  accountDetail: AccountDetailVO | null,
  enabled: boolean,
) => {
  // 当前选中的组织
  const [selectedOrg, setSelectedOrg] = useState<OrganizationDetailVO | null>(null);
  // 当前选中的角色
  const [selectedRole, setSelectedRole] = useState<OPSUserRoleVO | null>(null);
  // 员工绑定组织
  const [employeeOrganizations, setEmployeeOrganizations] = useState<OrganizationDetailVO[]>([]);
  // 每个组织下有哪些角色
  const rolesMap = useMemo(() => {
    return (
      accountDetail?.opsUserRoleList?.reduce(
        (acc, role) => {
          acc[role.orgId] = acc[role.orgId] ?? [];
          acc[role.orgId].push(role);
          return acc;
        },
        {} as Record<string, OPSUserRoleVO[]>,
      ) ?? {}
    );
  }, [accountDetail?.opsUserRoleList]);
  // 选中组织的角色列表
  const selectedOrgRoles = useMemo(() => {
    if (!selectedOrg) return [];
    return rolesMap[selectedOrg.orgId] ?? [];
  }, [rolesMap, selectedOrg]);

  /** 启用时拉取员工绑定组织 */
  useEffect(() => {
    if (!enabled || !userId) return;
    getEmployeeOrganizations(userId).then((res) => {
      setEmployeeOrganizations(res.data);
    });
  }, [enabled, userId]);

  /** 启用后默认选中第一个组织，关闭时清空 */
  useEffect(() => {
    if (!enabled) {
      setSelectedOrg(null);
      return;
    }
    if (!employeeOrganizations.length) {
      setSelectedOrg(null);
      return;
    }
    setSelectedOrg((prev) => {
      if (prev && employeeOrganizations.some((org) => org.orgId === prev.orgId)) {
        return prev;
      }
      return employeeOrganizations[0];
    });
  }, [enabled, employeeOrganizations]);

  /** 组织切换或角色列表变化时默认选中第一个角色，关闭时清空 */
  useEffect(() => {
    if (!enabled) {
      setSelectedRole(null);
      return;
    }
    if (!selectedOrgRoles.length) {
      setSelectedRole(null);
      return;
    }
    setSelectedRole((prev) => {
      if (!prev) return selectedOrgRoles[0];
      // 刷新详情后需同步最新角色对象（如 status），不能继续沿用旧引用
      const matched = selectedOrgRoles.find(
        (role) => getOpsUserRoleKey(role) === getOpsUserRoleKey(prev),
      );
      return matched ?? selectedOrgRoles[0];
    });
  }, [enabled, selectedOrg?.orgId, selectedOrgRoles]);

  return {
    selectedOrg,
    setSelectedOrg,
    selectedRole,
    setSelectedRole,
    employeeOrganizations,
    selectedOrgRoles,
    rolesMap,
  };
};
